import { FACTIONS, MATCH_SECONDS, type Faction } from './constants.ts';
import { canFactionRespawn, type Arena } from './arena.ts';
import type { PlayerState } from './player.ts';
import { isEmbodied } from './player.ts';

/**
 * `lobby` is the room before anybody has agreed to start.
 *
 * A phase rather than a screen, which is the whole of this change. The lobby
 * used to be a thing each client drew for itself out of local guesses — its own
 * roster of one, its own countdown, its own idea of who was ready — so two
 * people sitting in the same lobby could not see each other and either of them
 * hitting ready started a match for themselves alone. A room is the only thing
 * that can answer "who is here and are we going", so the room answers it.
 *
 * Nothing simulates in it. Movement, casting and the clock all wait for `live`
 * exactly as they do during a freeze.
 *
 * `over` ends a *round*, not necessarily the set.
 *
 * `intermission` is the scoreboard between rounds — the pause every shooter has
 * between the round ending and the next one starting, where the result lands,
 * the series score updates and somebody reads which map is next. It is a phase
 * rather than a timer on the side because everything already asks the phase
 * whether it should be running: casting, movement, chat scope and the reaper all
 * key off it, and a between-rounds state they had never heard of would have been
 * a live round to every one of them.
 */
export type MatchPhase = 'lobby' | 'warmup' | 'live' | 'over' | 'intermission';

export interface FactionScore {
  kills: number;
  poolsBiased: number;
  shrinesConquered: number;
  points: number;
}

export type WinReason = 'elimination' | 'timer' | 'last-shrine';

export interface MatchState {
  phase: MatchPhase;
  timeRemaining: number;
  eliminated: Faction[];
  winner: Faction | null;
  winReason: WinReason | null;
  scores: Record<Faction, FactionScore>;
}

/** Timer-expiry scoring. Conquering a shrine is worth far more than a kill. */
export const SCORE_WEIGHTS = { kill: 10, pool: 25, shrine: 150 } as const;

/**
 * @param seconds Round length. The lobby's time limit, when a lobby set one.
 */
export function makeMatch(seconds = MATCH_SECONDS): MatchState {
  const scores = {} as Record<Faction, FactionScore>;
  for (const f of FACTIONS) {
    scores[f] = { kills: 0, poolsBiased: 0, shrinesConquered: 0, points: 0 };
  }
  return {
    phase: 'warmup',
    timeRemaining: seconds > 0 ? seconds : MATCH_SECONDS,
    eliminated: [],
    winner: null,
    winReason: null,
    scores,
  };
}

export function recomputePoints(m: MatchState): void {
  for (const f of FACTIONS) {
    const s = m.scores[f];
    s.points =
      s.kills * SCORE_WEIGHTS.kill +
      s.poolsBiased * SCORE_WEIGHTS.pool +
      s.shrinesConquered * SCORE_WEIGHTS.shrine;
  }
}

export function factionsInPlay(players: PlayerState[]): Faction[] {
  const out: Faction[] = [];
  for (const p of players) if (!out.includes(p.faction)) out.push(p.faction);
  return out;
}

function livingCount(players: PlayerState[], faction: Faction): number {
  return players.filter((p) => p.faction === faction && isEmbodied(p)).length;
}

/**
 * A faction is out when its shrine has fallen *and* nobody is left standing.
 * While the shrine holds, its dead keep coming back, so killing everyone is
 * only ever temporary — which is what makes shrines the real objective.
 */
export function checkElimination(
  m: MatchState, arena: Arena, players: PlayerState[], faction: Faction,
): boolean {
  if (m.phase === 'over' || m.eliminated.includes(faction)) return false;
  if (!players.some((p) => p.faction === faction)) return false;
  if (canFactionRespawn(arena, faction)) return false;
  if (livingCount(players, faction) > 0) return false;

  m.eliminated.push(faction);
  return true;
}

/** How many sides have to turn up before a round is a round. */
export const SIDES_TO_PLAY = 2;

export function checkVictory(m: MatchState, players: PlayerState[]): boolean {
  if (m.phase === 'over') return false;

  const sides = factionsInPlay(players);
  /*
   * You cannot win a fight nobody else came to.
   *
   * With one player in a room this used to fire on the first live tick:
   * `factionsInPlay` returned the one side that had anybody on it, that was the
   * only side remaining, and the round ended by elimination — then the
   * scoreboard ran, the next round started, and it ended the same way. A player
   * alone on a server sat in a loop of ten-second intermissions with a clock
   * that counted and a match that never happened, unable to move because
   * nothing simulates between rounds.
   *
   * `checkElimination` already knew this rule — it refuses to eliminate a side
   * nobody joined — but victory was decided by counting what was left rather
   * than by anybody being beaten, so the guard on one end never reached the
   * other.
   */
  if (sides.length < SIDES_TO_PLAY) return false;

  const remaining = sides.filter((f) => !m.eliminated.includes(f));
  if (remaining.length === 1) {
    m.phase = 'over';
    m.winner = remaining[0];
    m.winReason = 'elimination';
    return true;
  }
  return false;
}

/** Timer expiry. Highest points wins; ties break on shrines, then pools. */
export function resolveOnTimer(m: MatchState, players: PlayerState[]): void {
  recomputePoints(m);
  const contenders = factionsInPlay(players).filter((f) => !m.eliminated.includes(f));
  if (contenders.length === 0) {
    m.phase = 'over';
    m.winner = null;
    m.winReason = 'timer';
    return;
  }
  contenders.sort((a, b) => {
    const sa = m.scores[a];
    const sb = m.scores[b];
    return (
      sb.points - sa.points ||
      sb.shrinesConquered - sa.shrinesConquered ||
      sb.poolsBiased - sa.poolsBiased
    );
  });
  m.phase = 'over';
  m.winner = contenders[0];
  m.winReason = 'timer';
}

export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
